import { createContext, useCallback, useEffect } from 'react';
import { useStateIfMounted } from 'use-state-if-mounted';
import { getBalance } from '@wagmi/core';

import wagmiConfig from 'wagmiConfig';
import { BALANCE_TOKEN_LIST } from 'constants/index';
import { usePriceContext } from './PriceContext';
import useWalletContext from 'hooks/useWalletContext';
import useWindowFocus from 'hooks/useWindowFocus';
import { formatNumber, setBalanceWithDecimals } from 'utils/numbers';

export const BalanceContext = createContext(null);

export const BalanceProvider = ({ children }) => {
  const [balances, setBalances] = useStateIfMounted([]);
  const { address, chainId, provider } = useWalletContext();
  const { coinPrice, nrgPrice, bonePrice, apePrice, beraPrice } = usePriceContext();
  const active = useWindowFocus();

  const totalBalance = balances?.reduce((a, b) => a + b.value, 0);

  const getTokenBalances = useCallback(
    async (retryCount = 0) => {
      try {
        const promise = BALANCE_TOKEN_LIST.map(async (token) => {
          let balance = 0;
          let result;
          if (token.chains.includes(chainId)) {
            // Check if token is native on this specific chain
            const isNativeOnThisChain =
              token.isNative && (!token.addresses || !token.addresses[chainId]);

            if (isNativeOnThisChain) {
              // For native tokens (like ETH or NRG on Energi chains)
              result = await getBalance(wagmiConfig, { address, chainId });
            } else {
              // For ERC20 tokens (like NRG on Ethereum)
              let tokenAddress = token.addresses?.[chainId];
              if (tokenAddress) {
                result = await getBalance(wagmiConfig, {
                  address,
                  token: tokenAddress,
                  chainId,
                });
              }
            }
          }
          if (result?.value) {
            balance = setBalanceWithDecimals(result.value, result.decimals);
          }
          // Use specific price for each token type
          let tokenPrice = coinPrice; // Default fallback

          if (token.symbol === 'NRG' || token.symbol === 'WNRG') {
            tokenPrice = nrgPrice || coinPrice || 0;
          } else if (token.symbol === 'BONE' || token.symbol === 'WBONE') {
            tokenPrice = bonePrice || coinPrice || 0;
          } else if (token.symbol === 'APE' || token.symbol === 'WAPE') {
            tokenPrice = apePrice || coinPrice || 0;
          } else if (token.symbol === 'BERA' || token.symbol === 'WBERA') {
            tokenPrice = beraPrice || coinPrice || 0;
          } else if (token.symbol === 'GMICV' || token.symbol === 'GMI') {
            tokenPrice = 0;
          } else if (token.symbol === 'apeUSD') {
            tokenPrice = 1;
          }

          return {
            ...token,
            balance: formatNumber(Number(balance)),
            last_price: tokenPrice,
            value: Number(balance) * tokenPrice,
          };
        });
        const balances = await Promise.all(promise);

        // Filter by balance > 0 instead of value > 0 to show tokens even if price is unavailable
        const assetsWithBalance = balances.filter(
          ({ balance }) => balance && Number(balance.replace(/,/g, '')) > 0,
        );

        // Always include native and wrapped tokens for the current chain even if balance is 0
        const nativeToken = balances.find(
          (token) => token.isNative && token.chains.includes(chainId),
        );
        const wrappedToken = balances.find(
          (token) =>
            (token.isWrapped || token.symbol.startsWith('W')) && token.chains.includes(chainId),
        );

        // Ensure native and wrapped tokens are always in the list
        const finalBalances = [...assetsWithBalance];
        if (nativeToken && !assetsWithBalance.find((t) => t.symbol === nativeToken.symbol)) {
          finalBalances.push(nativeToken);
        }
        if (wrappedToken && !assetsWithBalance.find((t) => t.symbol === wrappedToken.symbol)) {
          finalBalances.push(wrappedToken);
        }

        setBalances(finalBalances);
      } catch (error) {
        console.error('Error fetching balances:', error);

        if (retryCount < 3) {
          setTimeout(() => getTokenBalances(retryCount + 1), 2000);
        }
      }
    },
    [address, chainId, coinPrice, nrgPrice, bonePrice, apePrice, beraPrice, provider],
  );

  // Single useEffect to fetch balances when wallet/chain changes
  // Only refetch when address, chain, or window focus changes
  useEffect(() => {
    if (active && address && chainId) {
      getTokenBalances();
    }
  }, [active, address, chainId, getTokenBalances]);

  // Find native and wrapped tokens dynamically based on current chain
  const nativeToken = balances.find((token) => token.isNative && token.chains.includes(chainId));
  const wrappedToken = balances.find(
    (token) => (token.isWrapped || token.symbol.startsWith('W')) && token.chains.includes(chainId),
  );

  return (
    <BalanceContext.Provider
      value={{
        totalBalance,
        balances,
        getTokenBalances,
        wrappedBalance: wrappedToken?.balance,
        nativeBalance: nativeToken?.balance,
        coinLastPrice: nativeToken?.last_price,
      }}
    >
      {children}
    </BalanceContext.Provider>
  );
};
